import React from 'react';
import { Button } from 'src/components/Button';
import { ConditionItem } from './ConditionItem';
import {
  CallCondition,
  PostCallContainsCondition,
  PostCallJsonPropertyCondition,
  UiCondition,
  UiConditionType,
  UnselectedCondition,
  generateConditionId,
} from './types';
import { HumanDetectionResult } from 'src/interfaces/call.interface';

type ConditionsListProps = {
  conditions: UiCondition[];
  onChange: (next: UiCondition[]) => void;
  disabled?: boolean;
  labels: {
    withConditionsDescription: string;
    noConditionsDescription: string;
    noConditionsStateText: string;
  };
};

export const ConditionsList: React.FC<ConditionsListProps> = ({
  conditions,
  onChange,
  disabled = false,
  labels: {
    withConditionsDescription,
    noConditionsDescription,
    noConditionsStateText,
  },
}) => {
  const handleAdd = () => {
    const newCondition: UnselectedCondition = {
      id: generateConditionId(),
      type: UiConditionType.UNSELECTED,
    };
    onChange([...(conditions || []), newCondition]);
  };

  const handleSelectType = (index: number, nextType: UiConditionType) => {
    const next = [...conditions];
    if (nextType === UiConditionType.CALL) {
      next[index] = {
        id: next[index].id,
        type: UiConditionType.CALL,
        humanDetectionResult: HumanDetectionResult.NO_HUMAN,
      } as CallCondition;
    } else if (nextType === UiConditionType.POST_CALL_CONTAINS) {
      next[index] = {
        id: next[index].id,
        type: UiConditionType.POST_CALL_CONTAINS,
        searchString: '',
      } as PostCallContainsCondition;
    } else if (nextType === UiConditionType.POST_CALL_JSON_PROPERTY) {
      next[index] = {
        id: next[index].id,
        type: UiConditionType.POST_CALL_JSON_PROPERTY,
        path: '',
        value: '',
      } as PostCallJsonPropertyCondition;
    }
    onChange(next);
  };

  const handleChangeAt = (index: number, updated: UiCondition) => {
    const next = [...conditions];
    next[index] = updated;
    onChange(next);
  };

  const handleDeleteAt = (index: number) => {
    const next = [...conditions];
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <strong>Conditions</strong>
        <p className="text-neutral-400 text-sm">
          {conditions.length > 0
            ? withConditionsDescription
            : noConditionsDescription}
        </p>
      </div>

      {conditions.length === 0 ? (
        <div className="bg-neutral-50 rounded-lg p-6 text-center">
          <p className="text-neutral-400 text-sm">{noConditionsStateText}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {conditions.map((cond, idx) => (
            <ConditionItem
              key={cond.id}
              condition={cond}
              onChange={(next) => handleChangeAt(idx, next)}
              onDelete={() => handleDeleteAt(idx)}
              onSelectType={(t) => handleSelectType(idx, t)}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      <div>
        <Button
          variant="outlined"
          data-testid="button-add-condition"
          onClick={handleAdd}
          disabled={disabled}
          className="whitespace-nowrap"
          size="small"
        >
          Add condition
        </Button>
      </div>
    </div>
  );
};
